import { QueryCache, QueryClient, type Query } from '@tanstack/react-query';
import { inferenceHttp } from '@/lib/api/http-client.ts';
import { notificationService } from '@/lib/services/notification.service';

const STALE_TIME = 5 * 60 * 1000;

/**
 * Type-safe shape for `useQuery({ meta })`. Augmenting the TanStack
 * `Register` interface makes `query.meta?.errorMessage` typed everywhere
 * — no `unknown` casts at consumer call sites.
 *
 * Adding a new meta field: extend `AppQueryMeta` here and the whole app
 * gets the new field on every `useQuery` / `queryOptions` call site.
 */
interface AppQueryMeta extends Record<string, unknown> {
  /**
   * When set, the QueryCache global `onError` handler fires
   * `notificationService.showError(errorMessage, { preventDuplicate: true })`.
   * When ABSENT, query errors are silent (consumer handles them locally).
   *
   * Known limitation: re-toast gating is driven by
   * `query.state.dataUpdateCount`, which TanStack Query increments on ANY
   * data update — real fetch successes AND `queryClient.setQueryData(...)`
   * writes (i.e. optimistic updates). An optimistic write to a query key
   * that carries `errorMessage` therefore counts as a "recovery" for the
   * gate, and the next real fetch failure on that key will re-toast.
   *
   * This is an accepted limitation: no shipped consumer currently
   * combines optimistic writes with `meta.errorMessage` on the SAME key.
   * Consumers that need to mix both should own error surfacing locally
   * (per-hook `onError` in a wrapping mutation or a component-level
   * error boundary) rather than rely on this global toast.
   */
  errorMessage?: string;
}

// NOTE: only `queryMeta` is augmented here. `mutationMeta` was
// previously included but MISLEADING — no `MutationCache({ onError })`
// is wired, so `useMutation({ meta: { errorMessage } })` would type-check
// while silently NOT toasting. If mutation-level error toasts are needed
// later, add a `MutationCache({ onError: buildMutationCacheOnError() })`
// on the exported `queryClient` and re-augment `mutationMeta` in tandem.
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: AppQueryMeta;
  }
}

/**
 * Builds the QueryCache `onError` handler used by the app-wide
 * `queryClient`. Extracted so tests can wire it into a sibling
 * `QueryClient` without touching the singleton.
 *
 * Contract:
 *   - Reads `query.meta?.errorMessage` (typed via the `Register`
 *     augmentation above).
 *   - When present → toast on the FIRST error of each fresh error
 *     streak (i.e. entering an error state from success or from
 *     first-ever fetch). Subsequent refetch failures within the same
 *     streak are silenced.
 *   - When absent → no-op (silent path; consumer owns error UX).
 *
 * Per-client isolation: the `WeakMap` that tracks
 * per-Query cursors lives INSIDE this factory as closure state. Each
 * `buildQueryCacheOnError()` invocation gets its own map, so every
 * `QueryClient` built through this factory has an isolated cursor
 * space. No hidden module-level singleton — safe for SSR, Storybook,
 * multi-client setups, and test isolation.
 *
 * How the gate works: we gate on per-Query
 * `dataUpdateCount` rather than `errorUpdateCount === 1`. The old
 * `errorUpdateCount` gate suppressed EVERY error after the first one
 * for the life of the cache entry, so a query that recovered and then
 * failed again (a real new outage) never re-toasted. `dataUpdateCount`
 * increments on data updates (see limitation below):
 *   - While the query stays errored and refetches keep failing, the
 *     count is unchanged → seenAt matches → skip, so a
 *     refetchOnWindowFocus loop cannot re-toast.
 *   - After a successful fetch, the count changes → the next failure
 *     no longer matches → toast fires again.
 *   - First-ever error: no map entry (`undefined`) vs a `dataUpdateCount`
 *     of `0` → not equal → toast fires.
 *
 * Known limitation: `dataUpdateCount` is ALSO bumped
 * by `queryClient.setQueryData(key, ...)` (i.e. optimistic updates), not
 * just by real fetch successes. So an optimistic write to a query key
 * that carries `meta.errorMessage` shifts the cursor and looks like a
 * "recovery" to this gate — the next real fetch failure on that key
 * will re-toast even if the previous failure was still pending. No
 * shipped consumer currently combines optimistic updates with
 * `meta.errorMessage` on the SAME key, so this is a latent/theoretical
 * edge, not an active bug. Consumers that need to mix both patterns on
 * the same key should surface errors locally (per-hook `onError` in a
 * wrapping mutation or a component-level error boundary) rather than
 * rely on this global toast.
 *
 * `WeakMap` keyed on the `Query` instance is GC-safe: when the cache
 * entry is evicted, the map entry is collected too. No manual cleanup.
 *
 * `preventDuplicate: true` still guards the same-tick fan-out case
 * (many failing rows during one outage collapsing to a single toast).
 */
export function buildQueryCacheOnError() {
  // Per-Query cursor tracking the `dataUpdateCount` observed the last
  // time we toasted for THIS query instance. Lives inside the factory
  // (closure state) so each `QueryClient` gets its own isolated map —
  // no shared module-level singleton.
  const toastedAtDataCount = new WeakMap<Query<unknown, unknown, unknown>, number>();

  return (_error: unknown, query: Query<unknown, unknown, unknown>) => {
    const errorMessage = query.meta?.errorMessage;
    if (typeof errorMessage !== 'string' || errorMessage.length === 0) return;

    const seenAt = toastedAtDataCount.get(query);
    if (seenAt === query.state.dataUpdateCount) return;

    toastedAtDataCount.set(query, query.state.dataUpdateCount);
    notificationService.showError(errorMessage, { preventDuplicate: true });
  };
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: buildQueryCacheOnError(),
  }),
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: STALE_TIME,
      queryFn: async ({ queryKey }) => {
        const [url] = queryKey as [string, ...unknown[]];
        const { data } = await inferenceHttp.instance.get<unknown>(url);
        return data;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// Expose the query client for debugging in development
if (import.meta.env.DEV) {
  // @ts-expect-error -- Intentionally adding to the global scope for debugging purposes
  window.queryClient = queryClient;
}
